<template>
    <div ref="contextMenu" class="context-menu" v-bind:class="{ 'top': location.indexOf('top') > -1, 'float': location.indexOf('float') > -1 }" v-show="openMenu">
        <div class="dropdown-menu d-block position-relative">
            <slot v-bind:items="items">
                <a v-for="(item, index) in items" :key="index" class="dropdown-item cursor-pointer" :class="item.class" @click="item.callback ? item.callback(currentItem) : $emit('select-menu-item', currentItem, $event)">{{ item.title }}</a>
            </slot>
        </div>
    </div>
</template>
<script>
import { animationFrame } from "@/utils/browser";

export default {
    props: {
        items: {
            required: false,
            type: Array
        },
        location: {
            required: false,
            default: "bottom left",
            type: String
        }
    },
    name: "context-menu",
    data() {
        return {
            openMenu: false,
            currentItem: null,
            attachedElement: false,
            attachedPosition: false,
            resizeObserver: null
        };
    },
    created() {
        window.addEventListener("resize", this.updatePosition, true);
        window.addEventListener("scroll", this.updatePosition, true);
        window.addEventListener("click", this.closeTrigger);
    },
    mounted() {
        this.observeResize();
    },
    beforeDestroy () {
        this.stopObserverResize();
    },
    destroyed() {
        window.removeEventListener("resize", this.updatePosition);
        window.removeEventListener("scroll", this.updatePosition);
        window.removeEventListener("click", this.closeTrigger);
    },
    watch: {
    },
    methods: {
        open($event, item) {
            $event.stopPropagation();

            // clicked on menu item
            if (item == null) {
                this.openMenu = false;
                return;
            }

            this.currentItem = item;
            this.openMenu = true;
            this.attachedPosition = false;
            this.attachedElement = false;

            const target = $event.target;
            if (target) {
                // reopen at position
                this.attachedElement = target;

                // wait for vue js to render
                this.$nextTick(() => {
                    this.updatePosition(true);
                    this.observeResize();
                });
            } else {
                this.$nextTick(() => {
                    this.place($event.pageX, $event.pageY);
                });
            }
        },
        openAt(x, y, item) {
            this.openMenu = true;
            this.currentItem = item;
            this.attachedPosition = { x, y };

            // wait for vue js to render
            this.$nextTick(() => {
                this.place(x, y);
            });
        },
        place(x, y) {
            // place only if menu is open and context menu is valid object so we can calculate sizing properly
            if (!this.openMenu || !this.$refs.contextMenu) {
                return;
            }

            const contextMenuRect = this.$refs.contextMenu.getBoundingClientRect();

            let left = x;
            let top = y;

            const width = window.innerWidth;
            const height = window.innerHeight;
            const contextMenuWidth = contextMenuRect.width;
            const contextMenuHeight = contextMenuRect.height;
            const padBody = 8;

            // align to right location
            if (this.location.indexOf("right") > -1) {
                left -= contextMenuWidth;
            }

            if (this.location.indexOf("top") > -1) {
                top -= contextMenuHeight;
            }

            // non floating context menu will not reposition outside
            if (this.location.indexOf("float") < 0) {
                // check boundaries
                if (left < padBody) {
                    left = padBody;
                } else if (left + contextMenuWidth > width - padBody) {
                    left = width - contextMenuWidth - padBody;
                }

                if (top < padBody) {
                    top = padBody;
                } else if (top + contextMenuHeight > height - padBody) {
                    top = height - contextMenuHeight - padBody;
                }
            }

            // correct on iphone / ios to place to correct location
            if (window.visualViewport && window.visualViewport.width > 0 && window.visualViewport.height > 0) {
                top += window.visualViewport.offsetTop;
                left += window.visualViewport.offsetLeft;
            }

            // place
            this.$refs.contextMenu.style.top = top + "px";
            this.$refs.contextMenu.style.left = left + "px";

            // match parent to float in right position
            if (this.location.indexOf("float") > -1 && this.attachedElement) {
                this.$refs.contextMenu.style.width = this.attachedElement.getBoundingClientRect().width + "px";

                // check if menu is overflowing screen
                if (top + contextMenuHeight + (padBody * 2) > height) {
                    this.$refs.contextMenu.style.height = (height - top - padBody) + "px";
                } else {
                    this.$refs.contextMenu.style.height = "auto";
                }
            }
        },
        close() {
            this.openMenu = false;
            this.currentItem = null;
            this.attachedElement = false;
            this.attachedPosition = false;
            this.$emit("closed", true);
        },
        toggle($event, item) {
            // clicked on same element, act as toggle
            if (this.openMenu && item === this.currentItem) {
                this.openMenu = !this.openMenu;
                return;
            }

            this.open($event, item);
        },
        updatePosition(quick) {
            // this only works when open(event) is used
            if (!this.openMenu || (!this.attachedElement && !this.attachedPosition)) {
                return;
            }

            // update position
            animationFrame(() => {
                if (this.attachedElement) {
                    // calculate target place
                    const rect = this.attachedElement.getBoundingClientRect();
                    this.place(
                        rect.left + (this.location.indexOf("right") > -1 ? rect.width : 0),
                        rect.top + (this.location.indexOf("top") > -1 ? 0 : rect.height)
                    );
                    return;
                }

                if (this.attachedPosition) {
                    // restore position
                    this.place(this.attachedPosition.x, this.attachedPosition.y);
                }
            });
        },
        closeTrigger() {
            this.close();
        },
        // used to notify when context menu changes size
        observeResize() {
            if (typeof ResizeObserver === "undefined") {
                // unsupported browser
                return;
            }
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }
            this.resizeObserver = new ResizeObserver(entries => {
                entries.forEach(entry => {
                    // is visible
                    if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
                        this.updatePosition();
                    }
                });
            });
            this.resizeObserver.observe(this.$refs.contextMenu);
        },
        stopObserverResize() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }
        }
    }
};
</script>
<style lang="scss">
.context-menu {
    z-index: 10;
    position: fixed;

    /* default location to avoid shake on initial load */
    top: -9999px;
    left: -9999px;

    > .drop-down-menu > .menu {
        /* this is needed so that bounding client rect is calculated correctly */
        position: relative;
    }

    &.float > .drop-down-menu {
        height: 100%;

        > .menu {
            height: 100%;

            > ul {
                max-height: 100%;
                display: block;
                overflow: auto;
            }
        }
    }
}
</style>
